import PropTypes from "prop-types";
import get from "lodash/get";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { CheckIcon, PlusIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Field, FieldError } from "@/components/ui/field";
import { PhoneInput } from "@/components/ui/phone-input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FormInput, FormSelect, RemoveButton } from "components/BaseForm";
import ValidatePhoneNumberModal from "components/modals/ValidatePhoneNumberModal";
import CONSTANTS from "constants/Constants";

const PHONE_TYPES = CONSTANTS.CONTACT_PHONE_TYPES;

const PHONE_TYPE_OPTIONS = Object.keys(PHONE_TYPES).map(key => ({
  key: PHONE_TYPES[key],
  value: PHONE_TYPES[key],
  text: key,
}));

// A user's number stays disabled until it passes Twilio validation.
const blankUserPhone = {
  number: "",
  ext: "",
  type: PHONE_TYPES.DEFAULT,
  is_primary: false,
  enabled: false,
  is_callable: true,
};

// Turning one row's primary on turns the rest off, so at most one row is
// primary.
const PrimaryToggle = ({ index }) => {
  const { control, getValues, setValue } = useFormContext();
  const checked = useWatch({
    control,
    name: `phone_numbers.${index}.is_primary`,
  });
  return (
    <Switch
      checked={!!checked}
      aria-label={`Phone ${index + 1} primary`}
      onCheckedChange={next => {
        if (next) {
          getValues("phone_numbers").forEach((_, i) =>
            setValue(`phone_numbers.${i}.is_primary`, i === index)
          );
        } else {
          setValue(`phone_numbers.${index}.is_primary`, false);
        }
      }}
    />
  );
};

PrimaryToggle.propTypes = {
  index: PropTypes.number.isRequired,
};

const PhoneNumberInput = ({ index }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const name = `phone_numbers.${index}.number`;
  const error = get(errors, name);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Field data-invalid={error ? true : undefined}>
          <PhoneInput
            defaultCountry="US"
            placeholder="Enter phone number"
            aria-label={`Phone ${index + 1}`}
            value={field.value ?? undefined}
            onChange={value => field.onChange(value || null)}
            onBlur={field.onBlur}
            aria-invalid={error ? true : undefined}
          />
          <FieldError>{error?.message}</FieldError>
        </Field>
      )}
    />
  );
};

PhoneNumberInput.propTypes = {
  index: PropTypes.number.isRequired,
};

const ValidationStatus = ({ index }) => {
  const { control, setValue } = useFormContext();
  const phoneNumber = useWatch({ control, name: `phone_numbers.${index}` });

  if (phoneNumber.enabled) {
    return (
      <CheckIcon
        aria-label={`Phone ${index + 1} validated`}
        className="tw:size-4 tw:text-success"
      />
    );
  }
  return (
    <ValidatePhoneNumberModal
      phoneNumber={phoneNumber}
      onClose={status =>
        setValue(`phone_numbers.${index}.enabled`, status === "success")
      }
    />
  );
};

ValidationStatus.propTypes = {
  index: PropTypes.number.isRequired,
};

const UserPhoneNumberFields = () => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "phone_numbers",
  });

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      {fields.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="tw:min-w-64">Number</TableHead>
              <TableHead className="tw:w-24">Ext</TableHead>
              <TableHead className="tw:w-44">Type</TableHead>
              <TableHead className="tw:w-24">Primary</TableHead>
              <TableHead className="tw:w-36">Validated</TableHead>
              <TableHead className="tw:w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((field, index) => (
              <TableRow key={field.id}>
                <TableCell>
                  <PhoneNumberInput index={index} />
                </TableCell>
                <TableCell>
                  <FormInput
                    name={`phone_numbers.${index}.ext`}
                    aria-label={`Phone ${index + 1} extension`}
                    placeholder="Ext."
                  />
                </TableCell>
                <TableCell>
                  <FormSelect
                    name={`phone_numbers.${index}.type`}
                    options={PHONE_TYPE_OPTIONS}
                  />
                </TableCell>
                <TableCell>
                  <PrimaryToggle index={index} />
                </TableCell>
                <TableCell>
                  <ValidationStatus index={index} />
                </TableCell>
                <TableCell>
                  <RemoveButton
                    label={`Remove phone ${index + 1}`}
                    onClick={() => remove(index)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <p className="tw:text-sm tw:text-muted-foreground">No phone numbers</p>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="tw:self-start"
        onClick={() => append({ ...blankUserPhone })}
      >
        <PlusIcon data-icon="inline-start" /> Add Phone Number
      </Button>
    </div>
  );
};

export default UserPhoneNumberFields;
